/**
 * ActivityReport - Admin view for analytics activity.
 * Fetches GET /api/v1/reports/activity with admin key and displays events in a filterable table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PageView from '../common/pageView';
import { getActivityReport } from '../../service/reportsService';
import { ACTIVITY_EVENT_NAMES } from '../../utils/analyticsConstants';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import componentStyles from './activityReport.module.css';
import globalStyles from '../globals.module.css';

const REPORT_ID = 'activity';
const LIMIT_OPTIONS = [100, 500, 1000, 2000];
const DEFAULT_LIMIT = 500;

// Default date range: last 7 days
const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10)
    };
};

const formatDateTime = (isoStr) => {
    if (!isoStr) return '—';
    try {
        const d = new Date(isoStr);
        return isNaN(d.getTime()) ? isoStr : d.toLocaleString();
    } catch {
        return isoStr;
    }
};

const truncateId = (id, len = 8) => {
    if (!id) return '—';
    const s = String(id);
    return s.length <= len ? s : s.slice(0, len) + '…';
};

const ActivityReport = ({ embedded = false, refreshToken = 0 }) => {
    const { startDate: defaultStart, endDate: defaultEnd } = getDefaultDateRange();
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [eventName, setEventName] = useState('');
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [userId, setUserId] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const [events, setEvents] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    const [expandedProperties, setExpandedProperties] = useState(new Set());

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        setErrorStatus(null);
        try {
            const params = {
                id: REPORT_ID,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                limit
            };
            if (eventName) params.eventName = eventName;
            if (userId.trim()) params.userId = userId.trim();
            if (sessionId.trim()) params.sessionId = sessionId.trim();

            const response = await getActivityReport(params);
            const { data } = response;
            if (data.success) {
                setEvents(data.events || []);
                setCount(data.count ?? 0);
            } else {
                setError(data.error || 'Request failed');
            }
        } catch (err) {
            const status = err.response?.status;
            setErrorStatus(status);
            if (status === 401) {
                setError('You don\'t have access to this report. Check that the admin key is set correctly.');
            } else if (status === 400) {
                setError(err.response?.data?.error || 'Invalid request.');
            } else {
                setError(err.message || 'Something went wrong. Please try again.');
            }
            setEvents([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, eventName, limit, userId, sessionId]);

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshToken]);

    const handleApply = (e) => {
        e.preventDefault();
        fetchReport();
    };

    const toggleProperties = (index) => {
        setExpandedProperties((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const renderFilters = () => (
        <form onSubmit={handleApply} className={componentStyles.filtersRow}>
            <div className={componentStyles.filterGroup}>
                <label>Start date</label>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={globalStyles.standardInput}
                />
            </div>
            <div className={componentStyles.filterGroup}>
                <label>End date</label>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={globalStyles.standardInput}
                />
            </div>
            <div className={componentStyles.filterGroup}>
                <label>Event name</label>
                <select
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    className={globalStyles.standardSelect}
                >
                    <option value="">All</option>
                    {ACTIVITY_EVENT_NAMES.map((name) => (
                        <option key={name} value={name}>{name}</option>
                    ))}
                </select>
            </div>
            <div className={componentStyles.filterGroup}>
                <label>Limit</label>
                <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className={globalStyles.standardSelect}
                >
                    {LIMIT_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                    ))}
                </select>
            </div>
            <button
                type="submit"
                className={globalStyles.primaryButton}
                disabled={loading}
            >
                Apply
            </button>
            <button
                type="button"
                className={globalStyles.textButton}
                onClick={() => setShowAdvanced(!showAdvanced)}
            >
                {showAdvanced ? 'Hide advanced' : 'Advanced'}
            </button>
            {showAdvanced && (
                <div className={componentStyles.advancedFilters}>
                    <div className={componentStyles.filterGroup}>
                        <label>User ID</label>
                        <input
                            type="text"
                            placeholder="Optional"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            className={globalStyles.standardInput}
                        />
                    </div>
                    <div className={componentStyles.filterGroup}>
                        <label>Session ID</label>
                        <input
                            type="text"
                            placeholder="Optional"
                            value={sessionId}
                            onChange={(e) => setSessionId(e.target.value)}
                            className={globalStyles.standardInput}
                        />
                    </div>
                </div>
            )}
        </form>
    );

    const renderContent = () => {
        if (error) {
            return (
                <div className={`${componentStyles.errorBlock} ${errorStatus === 401 ? componentStyles.unauthorized : ''}`}>
                    <p>{error}</p>
                    <button type="button" className={globalStyles.secondaryButton} onClick={fetchReport}>
                        Retry
                    </button>
                </div>
            );
        }
        if (loading && events.length === 0) {
            return (
                <div className={globalStyles.loadingWrapper}>
                    <SmallLoadingAlt />
                </div>
            );
        }
        if (events.length === 0) {
            return (
                <div className={globalStyles.emptyState}>
                    No events match the current filters. Try a wider date range or clear event name.
                </div>
            );
        }
        return (
            <>
                <div className={componentStyles.summary}>
                    {count} event{count !== 1 ? 's' : ''}
                </div>
                <div className={componentStyles.tableWrap}>
                    <table className={componentStyles.eventsTable}>
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Event</th>
                                <th>Session</th>
                                <th>User</th>
                                <th>View</th>
                                <th>Prev view</th>
                                <th>Auth</th>
                                <th>Properties</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((ev, index) => (
                                <tr key={index}>
                                    <td>{formatDateTime(ev.timestamp)}</td>
                                    <td>{ev.event_name || '—'}</td>
                                    <td className={componentStyles.idCell} title={ev.session_id || ''}>
                                        {truncateId(ev.session_id)}
                                    </td>
                                    <td className={componentStyles.idCell} title={ev.user_id || ''}>
                                        {ev.user_id ? truncateId(ev.user_id) : '—'}
                                    </td>
                                    <td>{ev.view_name || '—'}</td>
                                    <td>{ev.prev_view || '—'}</td>
                                    <td>{ev.is_authenticated === true ? 'Yes' : ev.is_authenticated === false ? 'No' : '—'}</td>
                                    <td>
                                        {ev.properties && Object.keys(ev.properties).length > 0 ? (
                                            <span
                                                className={`${componentStyles.propertiesCell} ${expandedProperties.has(index) ? componentStyles.expanded : ''}`}
                                                onClick={() => toggleProperties(index)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => e.key === 'Enter' && toggleProperties(index)}
                                            >
                                                {expandedProperties.has(index)
                                                    ? JSON.stringify(ev.properties, null, 2)
                                                    : JSON.stringify(ev.properties)}
                                            </span>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    if (embedded) {
        return (
            <section className={`${globalStyles.standardSection} ${componentStyles.embeddedReport}`}>
                <h3 className={globalStyles.sectionHeader}>Activity Report</h3>
                {renderFilters()}
                {renderContent()}
            </section>
        );
    }

    return (
        <PageView
            title="Activity Report"
            header={renderFilters()}
            contentClassName={componentStyles.content}
        >
            {renderContent()}
        </PageView>
    );
};

export default ActivityReport;
